import { Navigate, useRoutes } from 'react-router-dom';
// auth

import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
// layouts
import CompactLayout from '../layouts/compact';
import DashboardLayout from '../layouts/dashboard';
// config
import { PATH_AFTER_LOGIN } from '../config-global';
import DashboardRedirect from './DashboardRedirect';
import PartnerRoleGuard from './PartnerRoleGuard';
import { PATH_DASHBOARD } from './paths';
//
import {
  Page404,
  ProfilePage,
  ManageAccount,
  LoginPage,
  RegisterPage,
  Customers,
  CustomerCreateEditPage,
  AccountManagerPage,
  Partners,
  PartnerCreateEditPage,
  PartnerAccountPage,
  Users,
  UserNewEditPage,
  Teams,
  TeamNewEditPage,
  WhatsAppSettingsPage,
  WhatsAppTemplatesPage,
  ReportsPage,
  GoogleWorkspace,
  CustomerInfo,
  Rates,
  Transactions,
  WhitelabelConfigPage,
  PartnersWallet,
  CustomersWallet,
  WalletTransactions,
  PartnerProfilePage,
  CustomizeDemoForm,
  DemoRecords,
  DiscoveryCallStup,
  FixDisplayName,
  NotificationsPage,
  BillingPaymentsForm,
  OnboardConfigPage,
  ApiToken,
  BillingConfigPage,
  CouponPage,
  CreateCoupon,
  CouponView,
  SupportChannelPage,
  CustomerAnalytics,
  SuperUserPage,
  DiscoveryCallList,
  DiscoveryDetails,
  AnalyticsPage,
  GmbPartners,
  GmbCustomers,
  GmbTransactions,
  GmbSetupApiKeyPage,
  GmbTransactionInvoice,
  MemberOrganizations,
} from './elements';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    {
      path: '/',
      children: [
        { element: <DashboardRedirect />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: '/manage-account/:accessToken/:refreshToken',
      element: <ManageAccount />,
    },
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <DashboardRedirect />, index: true },
        {
          path: 'profile',
          element: <ProfilePage />,
        },
        {
          path: 'customers',
          children: [
            { element: <Customers />, index: true },
            { path: 'add-customer', element: <CustomerCreateEditPage /> },
            {
              path: 'edit/:name',
              element: <CustomerCreateEditPage />,
            },
            {
              path: 'account-manager',
              element: <AccountManagerPage />,
            },
          ],
        },
        {
          path: 'partners',
          children: [
            { element: <Partners />, index: true },
            { path: 'profile', element: <PartnerProfilePage /> },
            { path: 'account-setup', element: <PartnerAccountPage /> },
            { path: 'add-partner', element: <PartnerCreateEditPage /> },
            {
              path: 'edit/:name',
              element: <PartnerCreateEditPage />,
            },
          ],
        },
        // {
        //   path: 'google-workspace',
        //   children: [
        //     { element: <GoogleWorkspace />, index: true },
        //     { path: 'info', element: <CustomerInfo /> },
        //   ],
        // },
        {
          path: 'google-workspace',
          children: [
            {
              path: 'customers',
              children: [
                { element: <GoogleWorkspace />, index: true },
                { path: 'info', element: <CustomerInfo /> },
              ],
            },
            { path: 'transactions', element: <Transactions /> },
          ],
        },
        {
          path: 'teams',
          children: [
            {
              element: (
                <PartnerRoleGuard allowedRoles={['super_admin', 'admin']}>
                  <Teams />
                </PartnerRoleGuard>
              ),
              index: true,
            },
            {
              path: 'new',
              element: (
                <PartnerRoleGuard allowedRoles={['super_admin', 'admin']}>
                  <TeamNewEditPage />
                </PartnerRoleGuard>
              ),
            },
            {
              path: 'edit/:id',
              element: (
                <PartnerRoleGuard allowedRoles={['super_admin', 'admin']}>
                  <TeamNewEditPage />
                </PartnerRoleGuard>
              ),
            },
          ],
        },
        {
          path: 'users',
          children: [
            {
              element: (
                <PartnerRoleGuard allowedRoles={['super_admin', 'admin']}>
                  <Users />
                </PartnerRoleGuard>
              ),
              index: true,
            },
            {
              path: 'add-user',
              element: (
                <PartnerRoleGuard allowedRoles={['super_admin', 'admin']}>
                  <UserNewEditPage />
                </PartnerRoleGuard>
              ),
            },
            {
              path: 'edit/:name',
              element: (
                <PartnerRoleGuard allowedRoles={['super_admin', 'admin']}>
                  <UserNewEditPage />
                </PartnerRoleGuard>
              ),
            },
          ],
        },
        {
          path: 'whatsapp',
          children: [
            { path: 'settings', element: <WhatsAppSettingsPage /> },
            { path: 'templates', element: <WhatsAppTemplatesPage /> },
            { path: 'rates', element: <Rates /> },
            { path: 'partners', element: <PartnersWallet /> },
            { path: 'customers', element: <CustomersWallet /> },
            { path: 'analytics', element: <AnalyticsPage /> },
            { path: 'customers/customer-analytics', element: <CustomerAnalytics /> },
            { path: 'transactions', element: <WalletTransactions /> },
            {
              path: 'demo',
              children: [
                { path: 'customize', element: <CustomizeDemoForm /> },
                { path: 'records', element: <DemoRecords /> },
              ],
            },
            {
              path: 'customize-demo',
              element: <Navigate to={PATH_DASHBOARD.whatsApp.demo.customize} replace />,
            },

            { path: 'fix-display-name', element: <FixDisplayName /> },
            { path: 'onboarding-config', element: <OnboardConfigPage /> },

            // {
            //   path: 'billing',
            //   children: [
            //     { path: 'billing-profile', element: <BillingProfile /> },
            //     { path: 'billing-payment', element: <BillingPayments /> }, // Fix: Correct component
            //     { path: 'customer-invoice', element: <CustomerInvoice /> },
            //     { path: 'create-payment-gateway', element: <BillingPaymentsForm /> },
            //     { path: 'edit-payment-gateway', element: <BillingPaymentsForm /> },
            //   ],
            // },
          ],
        },
        {
          path: 'google-my-business',
          children: [
            { path: 'partners', element: <GmbPartners /> },
            { path: 'customers', element: <GmbCustomers /> },
            { path: 'transactions', element: <GmbTransactions /> },
            { path: 'transaction-invoice', element: <GmbTransactionInvoice /> },
            { path: 'setup-api-key', element: <GmbSetupApiKeyPage /> },
          ],
        },
        {
          path: 'discovery-call',
          children: [
            { path: 'discovery-list', element: <DiscoveryCallList /> },
            { path: 'discovery-setup', element: <DiscoveryCallStup /> },
            { path: 'discovery-setup/:id', element: <DiscoveryDetails /> },
          ],
        },
        {
          path: 'notifications',
          element: <NotificationsPage />,
        },
        {
          path: 'support-channel',
          element: <SupportChannelPage />,
        },

        {
          path: 'reports',
          element: <ReportsPage />,
        },
        {
          path: 'developers',
          children: [{ path: 'api-token', element: <ApiToken /> }],
        },

        {
          path: 'whitelable-config',
          element: <WhitelabelConfigPage />,
        },
        {
          path: 'billing',
          element: <BillingConfigPage />,
          children: [
            // { path: 'billing-profile', element: <BillingProfile /> },
            // { path: 'billing-payment', element: <BillingPayments /> }, // Fix: Correct component
            // { path: 'customer-invoice', element: <CustomerInvoice /> },
            { path: 'create-payment-gateway', element: <BillingPaymentsForm /> },
            { path: 'edit-payment-gateway', element: <BillingPaymentsForm /> },
          ],
        },
        {
          path: 'coupons',
          children: [
            { element: <CouponPage />, index: true },
            { path: 'create-coupon', element: <CreateCoupon /> },
            { path: 'coupon-view/:id', element: <CouponView /> },
          ],
        },
        {
          path: 'user-org-management',
          element: (
            <PartnerRoleGuard allowedRoles={['super_admin', 'admin']}>
              <SuperUserPage />
            </PartnerRoleGuard>
          ),
        },
        {
          path: 'organizations',
          element: <MemberOrganizations />,
        },
        // {
        //   path: 'partners-profile',
        //   children: [
        //     { path: 'profile', element: <PartnerProfilePage /> },
        //     { path: 'billing-profile', element: <BillingProfile /> },
        //   ],
        // },
        // {
        //   path: 'leadsetup',
        //   element: <LeadSetup />,
        // },
      ],
    },
    {
      element: <CompactLayout />,
      children: [{ path: '404', element: <Page404 /> }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
